import {
  useState,
  useEffect,
  RefObject,
  useCallback,
  SetStateAction,
  useRef,
} from 'react';

export function useIsVerticalOverflow(
  scrollableRef: RefObject<HTMLElement>,
): boolean {
  const [hasScrollbar, setHasScrollbar] = useState<boolean>(false);

  useEffect(() => {
    const container = scrollableRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      const hasScrollbar = container.scrollWidth > container.clientWidth;
      setHasScrollbar(hasScrollbar);
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [scrollableRef]);

  return hasScrollbar;
}

export function useScrolledToRight(ref: React.RefObject<HTMLElement>): boolean {
  const [isScrolledToRight, setIsScrolledToRight] = useState(false);

  const handleScroll = useCallback(() => {
    if (ref.current) {
      const { scrollWidth, scrollLeft, clientWidth } = ref.current;
      // Check if the element is scrolled all the way to the right
      const scrolledToRight = scrollLeft + clientWidth >= scrollWidth - 30;
      setIsScrolledToRight(scrolledToRight);
    }
  }, [ref]);

  useEffect(() => {
    const current = ref.current;
    if (current) {
      current.addEventListener('scroll', handleScroll);
      // Initial check in case the element is already scrolled to the right
      handleScroll();
    }

    return () => {
      if (current) {
        current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [ref, handleScroll]);

  return isScrolledToRight;
}

export function useResizeObserver(
  ref: RefObject<HTMLElement>,
  callback: ResizeObserverCallback,
): void {
  useEffect(() => {
    const resizeObserver = new ResizeObserver(callback);
    const current = ref.current;

    if (current) {
      resizeObserver.observe(current);
    }

    return () => {
      if (current) {
        resizeObserver.unobserve(current);
      }
    };
  }, [ref, callback]);
}

export function useDebouncedState<T>(defaultValue: T, wait: number) {
  const [value, setValue] = useState(defaultValue);
  const timeoutRef = useRef<number | null>(null);

  const clearTimeout = () =>
    window.clearTimeout(timeoutRef.current ?? undefined);
  useEffect(() => clearTimeout, []);

  const debouncedSetValue = useCallback(
    (newValue: SetStateAction<T>, immediately?: boolean) => {
      clearTimeout();
      if (immediately) {
        setValue(newValue);
      } else {
        timeoutRef.current = window.setTimeout(() => {
          setValue(newValue);
        }, wait);
      }
    },
    [],
  );

  return [value, debouncedSetValue] as const;
}
